import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { RouterOutput } from '@trapeze/server';
import { AlertCircle, Calendar, CheckCircle, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

type Provider = RouterOutput['admin']['getProviders']['providers'][number];

interface EditProviderDialogProps {
  provider: Provider | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (providerId: string, data: { minAge?: number | null; maxAge?: number | null }) => void;
  isSaving?: boolean;
}

export function EditProviderDialog({
  provider,
  open,
  onOpenChange,
  onSave,
  isSaving = false,
}: EditProviderDialogProps) {
  const [minAge, setMinAge] = useState<number | null>(null);
  const [maxAge, setMaxAge] = useState<number | null>(null);

  // Reset form when provider changes or dialog opens
  useEffect(() => {
    if (provider && open) {
      setMinAge(provider.minAge);
      setMaxAge(provider.maxAge);
    }
  }, [provider, open]);

  const handleSave = () => {
    if (!provider) return;

    const updates: { minAge?: number | null; maxAge?: number | null } = {};

    // Only include fields that changed
    if (minAge !== provider.minAge) {
      updates.minAge = minAge;
    }

    if (maxAge !== provider.maxAge) {
      updates.maxAge = maxAge;
    }

    onSave(provider.id, updates);
  };

  const handleClearMinAge = () => {
    setMinAge(null);
  };

  const handleClearMaxAge = () => {
    setMaxAge(null);
  };

  // Validation: minAge must be less than or equal to maxAge if both are provided
  // Allow equal values for edge cases (e.g., provider only sees 18-year-olds)
  const hasValidationError = minAge !== null && maxAge !== null && minAge > maxAge;

  // Check if there are any changes
  const hasChanges = minAge !== provider?.minAge || maxAge !== provider?.maxAge;

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Provider Age Restrictions</DialogTitle>
          <DialogDescription>
            Configure age restrictions for{' '}
            <span className="font-medium">{provider.name || 'Unnamed Provider'}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Provider Info (Read-Only) */}
          <div className="grid gap-2">
            <Label>Provider Information</Label>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono">
                EHR ID: {provider.ehrId || 'N/A'}
              </Badge>
              <Badge
                variant={provider.canSchedule ? 'default' : 'secondary'}
                className="flex items-center gap-1"
              >
                {provider.canSchedule ? (
                  <>
                    <CheckCircle className="h-3 w-3" />
                    Can Schedule
                  </>
                ) : (
                  <>
                    <XCircle className="h-3 w-3" />
                    Cannot Schedule
                  </>
                )}
              </Badge>
            </div>
          </div>

          {/* Minimum Age Field */}
          <div className="grid gap-2">
            <Label htmlFor="minAge" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Minimum Age
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="minAge"
                type="number"
                placeholder="e.g., 0 (for newborns)"
                value={minAge ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const parsed = Number.parseInt(value, 10);
                  // Handle empty string or invalid input (NaN)
                  setMinAge(value === '' || Number.isNaN(parsed) ? null : parsed);
                }}
                min={0}
                max={150}
                className="flex-1"
              />
              {minAge !== null && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleClearMinAge}
                  className="shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Minimum age of patients this provider can see (leave empty for no minimum)
            </p>
          </div>

          {/* Maximum Age Field */}
          <div className="grid gap-2">
            <Label htmlFor="maxAge" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Maximum Age
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="maxAge"
                type="number"
                placeholder="e.g., 18 (for pediatric)"
                value={maxAge ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const parsed = Number.parseInt(value, 10);
                  // Handle empty string or invalid input (NaN)
                  setMaxAge(value === '' || Number.isNaN(parsed) ? null : parsed);
                }}
                min={0}
                max={150}
                className="flex-1"
              />
              {maxAge !== null && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleClearMaxAge}
                  className="shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Maximum age of patients this provider can see (leave empty for no maximum)
            </p>
          </div>

          {/* Validation Error */}
          {hasValidationError && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>Minimum age must be less than or equal to maximum age</span>
            </div>
          )}

          {/* Example Preview */}
          {!hasValidationError && (minAge !== null || maxAge !== null) && (
            <div className="rounded-md border border-muted bg-muted/50 p-3">
              <p className="text-sm font-medium">Preview:</p>
              <p className="text-sm text-muted-foreground">
                {minAge !== null && maxAge !== null
                  ? `Provider will see patients aged ${minAge}-${maxAge} years`
                  : minAge !== null
                    ? `Provider will see patients aged ${minAge}+ years`
                    : `Provider will see patients up to ${maxAge} years old`}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !hasChanges || hasValidationError}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
